import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Copy, Check, Video, Clock, Target, Zap, AlertTriangle, ChevronDown, ChevronUp, MessageSquare, Calendar, User, FileText, TrendingUp, DollarSign, Users, BarChart3, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

// Theme
const theme = {
  bg: '#f5f3f0',
  card: '#ffffff',
  border: '#e0dbd4',
  text: '#1a1a1a',
  textMuted: 'rgba(26,26,26,0.6)',
  orange: '#F05A1A',
  green: '#16a34a',
  yellow: '#d97706',
  red: '#dc2626',
  purple: '#7c3aed',
};

// Access code for Closer pages
const CLOSER_CODE = 'closer10';

// Lock Screen Component
const LockScreen = ({ onUnlock }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.toLowerCase() === CLOSER_CODE) {
      onUnlock();
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: theme.bg }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 max-w-md w-full text-center"
      >
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(124,58,237,0.1)' }}>
          <Lock className="w-8 h-8" style={{ color: theme.purple }} />
        </div>
        <h1 className="font-bebas text-3xl text-gray-800 mb-2">SCRIPT VISIO CLOSER</h1>
        <p className="text-gray-500 mb-6">Entrez le code d'accès Closer pour continuer</p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Code d'accès"
            className={`w-full px-4 py-3 rounded-xl border-2 text-center font-bold tracking-widest uppercase outline-none transition-colors ${
              error ? 'border-red-500 bg-red-50' : 'border-gray-200 focus:border-purple-500'
            }`}
            data-testid="closer-call-code-input"
          />
          {error && <p className="text-red-500 text-sm mt-2">Code incorrect</p>}
          <button
            type="submit"
            className="w-full mt-4 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: theme.purple }}
            data-testid="closer-call-unlock-btn"
          >
            DÉVERROUILLER
          </button>
        </form>
        
        <Link 
          to="/recrutement?tab=closer" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 text-sm mt-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au recrutement
        </Link>
      </motion.div>
    </div>
  );
};

// Copy button component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
      style={{ 
        backgroundColor: copied ? 'rgba(22,163,74,0.1)' : 'rgba(124,58,237,0.1)',
        color: copied ? theme.green : theme.purple,
        border: `1px solid ${copied ? 'rgba(22,163,74,0.3)' : 'rgba(124,58,237,0.3)'}`
      }}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copié !' : 'Copier'}
    </motion.button>
  );
};

// Script block component
const ScriptBlock = ({ phase, duration, title, content, note, copyText }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm"
  >
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span 
          className="text-[10px] font-bold uppercase px-2 py-1 rounded-full"
          style={{ backgroundColor: 'rgba(124,58,237,0.1)', color: theme.purple }}
        >
          {phase}
        </span>
        {duration && (
          <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-600">
            {duration}
          </span>
        )}
      </div>
      {copyText && <CopyButton text={copyText} />}
    </div>
    <h3 className="font-bold text-gray-800 mb-3">{title}</h3>
    <div 
      className="text-sm leading-relaxed p-4 rounded-lg whitespace-pre-line"
      style={{ backgroundColor: '#fafaf8', color: theme.text }}
    >
      {content}
    </div>
    {note && (
      <p className="text-xs mt-3 italic" style={{ color: theme.textMuted }}>
        → {note}
      </p>
    )}
  </motion.div>
);

// Objection card component
const ObjectionCard = ({ objection, response, isOpen, onClick }) => (
  <motion.div
    layout
    onClick={onClick}
    className="rounded-xl overflow-hidden cursor-pointer transition-all bg-white border"
    style={{ borderColor: isOpen ? theme.purple : '#e0dbd4' }}
  >
    <div className="p-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <span className="text-red-500">🚫</span>
        <span className="text-sm font-bold text-gray-700">{objection}</span>
      </div>
      <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
        <ChevronDown className="w-5 h-5" style={{ color: theme.purple }} />
      </motion.div>
    </div>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
        >
          <div className="px-4 pb-4 border-t border-gray-100 pt-3">
            <div className="flex justify-end mb-2">
              <CopyButton text={response} />
            </div>
            <div 
              className="text-sm leading-relaxed p-4 rounded-lg whitespace-pre-line"
              style={{ backgroundColor: '#fafaf8', color: theme.text }}
            >
              {response}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// Expandable section
const Section = ({ title, icon, children, defaultOpen = false, color = 'purple' }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const colorValue = color === 'purple' ? theme.purple : theme.orange;
  
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{icon}</span>
          <span className="font-bold text-gray-800">{title}</span>
        </div>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
          <ChevronDown className="w-5 h-5" style={{ color: colorValue }} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Rule card
const RuleCard = ({ icon, title, description }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-purple-50 border border-purple-200">
    <span className="text-lg">{icon}</span>
    <div>
      <p className="font-bold text-gray-800 text-sm">{title}</p>
      <p className="text-xs text-gray-600 mt-0.5">{description}</p>
    </div>
  </div>
);

// KPI Table Row
const KPIRow = ({ kpi, danger, correct, excellent }) => (
  <tr className="border-b border-gray-100">
    <td className="py-2 px-3 font-semibold text-gray-800 text-sm">{kpi}</td>
    <td className="py-2 px-3 text-center">
      <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-600 font-semibold">{danger}</span>
    </td>
    <td className="py-2 px-3 text-center">
      <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-semibold">{correct}</span>
    </td>
    <td className="py-2 px-3 text-center">
      <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-600 font-semibold">{excellent}</span>
    </td>
  </tr>
);

// Closing technique card
const ClosingCard = ({ title, content }) => (
  <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-5 border border-purple-200">
    <h4 className="font-bold text-purple-700 mb-3 flex items-center gap-2">
      <Target className="w-4 h-4" />
      {title}
    </h4>
    <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
      {content}
    </div>
  </div>
);

const CloserCallScriptPage = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [openObjection, setOpenObjection] = useState(null);

  const objections = [
    {
      objection: "C'est trop cher.",
      response: `Je comprends. Trop cher par rapport à quoi ?

[ Il répond ]

Tu m'as dit tout à l'heure que tu perdais environ [X€] par mois sur ton food cost / ta livraison / ta masse salariale.

Sur 90 jours, c'est [3X€]. L'accompagnement coûte [prix]. On est en train de te demander d'investir [prix] pour récupérer [3X€].

Le vrai risque, c'est de ne rien faire.`
    },
    {
      objection: "J'ai pas le budget en ce moment.",
      response: `Je t'entends. Et je vais être honnête avec toi : si t'as pas le budget c'est souvent le symptôme exact du problème qu'on vient de passer en revue ensemble.

La question c'est : tu veux attendre que ça s'améliore tout seul — ou tu veux qu'on travaille sur les fuites pour que le budget se libère ?

On peut regarder ensemble les options de paiement en plusieurs fois si ça aide.`
    },
    {
      objection: "J'ai besoin d'en parler à mon associé / ma femme / mon comptable.",
      response: `Bien sûr. Et pour que tu puisses en parler de façon claire — c'est quoi exactement le point sur lequel tu as besoin de son avis ?

[ Il répond ]

Ok. Si [associé / femme / comptable] donne son accord, toi personnellement t'es prêt à démarrer ?

[ Si oui ] : « Parfait. Je t'envoie un résumé du call par écrit que tu peux lui montrer. Et si tu veux, on fait un second call avec lui/elle cette semaine. »`
    },
    {
      objection: "J'ai déjà essayé des formations, ça n'a rien changé.",
      response: `C'est la réponse la plus fréquente. Et c'est logique — parce que 90% des formations restaurant sont théoriques.

Ce qu'on fait c'est différent : on travaille sur tes chiffres à toi, ton menu à toi, ton équipe à toi. Pas des cas génériques.

La vraie question c'est : est-ce que tu as déjà travaillé avec quelqu'un qui connaît les ratios de ta typologie de restaurant et qui t'a accompagné semaine par semaine ?

[ Il dit non ] : « C'est exactement ce qu'on fait. »`
    },
    {
      objection: "J'ai pas le temps de m'investir dans un accompagnement.",
      response: `Je comprends. Mais je vais retourner la question : combien d'heures par semaine est-ce que tu passes à gérer des problèmes que tu n'aurais pas si tes process étaient en place ?

[ Il réfléchit ]

L'accompagnement c'est 2h par semaine max — calls + implémentation. Le but c'est précisément de te faire gagner du temps, pas t'en prendre.`
    },
    {
      objection: "Je vais réfléchir.",
      response: `Bien sûr. Tu réfléchis à quoi exactement ? Sur le fond ou sur le timing ?

[ Il répond ]

Si c'est le fond : qu'est-ce qui te manque comme information pour te décider ?

Si c'est le timing : sachant que chaque mois tu perds [chiffre], c'est quoi le bon moment selon toi ?`
    },
    {
      objection: "Est-ce que ça marche vraiment ?",
      response: `Question légitime. Ce qui marche dépend de ce que tu mets dedans.

Les restaurateurs qu'on accompagne et qui obtiennent des résultats ont un point commun : ils appliquent. Ceux qui n'avancent pas attendent que ça se fasse tout seul.

Est-ce que toi t'es dans un état d'esprit d'appliquer — ou t'es en mode observation ?`
    },
    {
      objection: "Je connais déjà mes problèmes, j'ai pas besoin qu'on me les explique.",
      response: `Je te crois. Et connaître ses problèmes c'est déjà 30% du travail.

La vraie question c'est : qu'est-ce qui t'a empêché de les résoudre seul jusqu'ici ?

[ Il répond ]

C'est exactement ce que l'accompagnement adresse — pas le diagnostic, mais l'exécution.`
    }
  ];

  if (!isUnlocked) {
    return <LockScreen onUnlock={() => setIsUnlocked(true)} />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/recrutement?tab=closer" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la page Closer
          </Link>
          <h1 className="text-white font-bebas text-4xl tracking-wide flex items-center gap-3">
            <Video className="w-8 h-8" />
            SCRIPT VISIO CLOSER
          </h1>
          <p className="text-white/80 text-sm mt-2">
            Call de vente · Accompagnement 90 jours · Version 1.0
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        
        {/* Philosophie */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-6 border-2 border-purple-300"
        >
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-6 h-6 text-purple-600" />
            <h2 className="font-bebas text-2xl text-purple-600">PHILOSOPHIE DU CALL</h2>
          </div>
          <p className="text-gray-700 leading-relaxed font-semibold mb-3">
            Ce call n'est pas un argumentaire de vente. C'est un diagnostic approfondi.
          </p>
          <p className="text-gray-600 mb-4">
            Le prospect doit parler <strong>70% du temps</strong>. Toi 30%. Plus il formule ses problèmes à voix haute, plus il se convainc lui-même que ça doit changer.
          </p>
          <div className="bg-purple-100 rounded-lg p-4 space-y-2 text-sm">
            <p className="text-purple-800">• Tu n'es pas là pour convaincre. Tu es là pour comprendre et qualifier.</p>
            <p className="text-purple-800">• Si le profil ne correspond pas : tu le dis clairement. Ça renforce ta crédibilité.</p>
            <p className="text-purple-800">• Si le profil correspond : tu closes avec conviction. Pas de demi-mesure.</p>
          </div>
        </motion.div>

        {/* Préparation */}
        <Section title="PRÉPARATION AVANT LE CALL" icon="📋" defaultOpen>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-600" />
                Ce qu'il faut avoir sous les yeux
              </h4>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• Le score et les résultats du diagnostic audit-resto.fr</li>
                <li>• Le type de restaurant (street food, bistronomique, brasserie, livraison, etc.)</li>
                <li>• La ville / le marché local si possible</li>
                <li>• Ses posts Instagram récents — comprendre le positionnement, l'ambiance</li>
                <li>• Nombre d'avis Google et note moyenne</li>
                <li>• Le lien de paiement / contrat prêt à envoyer</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h4 className="font-bold text-yellow-700 mb-3">Questions à anticiper selon le type de resto</h4>
              <div className="grid gap-3 text-sm">
                <div>
                  <span className="font-semibold text-gray-800">Street food / snack</span>
                  <p className="text-gray-600">→ Food cost élevé, livraison importante, marge compressée</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-800">Bistronomique / gastronomique</span>
                  <p className="text-gray-600">→ Masse salariale lourde, ticket moyen élevé, positionnement</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-800">Brasserie / traditionnel</span>
                  <p className="text-gray-600">→ Volume, rotation tables, gestion des pics</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-800">Dark kitchen / livraison only</span>
                  <p className="text-gray-600">→ Commissions plateformes, dépendance Uber/Deliveroo</p>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Chiffres clés */}
        <Section title="CHIFFRES À MAÎTRISER (Benchmarks France)" icon="📊" defaultOpen>
          <p className="text-sm text-gray-600 mb-4">
            Le closer doit connaître ces chiffres par cœur. Ce sont les armes de la crédibilité.
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="py-2 px-3 text-left font-bold text-gray-800">KPI</th>
                  <th className="py-2 px-3 text-center font-bold text-red-600">⚠️ Danger</th>
                  <th className="py-2 px-3 text-center font-bold text-yellow-600">🟡 Correct</th>
                  <th className="py-2 px-3 text-center font-bold text-green-600">✅ Excellent</th>
                </tr>
              </thead>
              <tbody>
                <KPIRow kpi="Food Cost" danger="> 38%" correct="33-38%" excellent="< 30%" />
                <KPIRow kpi="Masse salariale" danger="> 40%" correct="35-40%" excellent="< 33%" />
                <KPIRow kpi="Prime Cost (FC + MS)" danger="> 70%" correct="65-70%" excellent="< 62%" />
                <KPIRow kpi="Loyer / CA" danger="> 12%" correct="8-12%" excellent="< 8%" />
                <KPIRow kpi="Marge nette" danger="< 5%" correct="5-10%" excellent="> 12%" />
                <KPIRow kpi="Livraison / CA total" danger="> 40%" correct="20-40%" excellent="< 20%" />
                <KPIRow kpi="Commission plateforme" danger="> 30%" correct="25-30%" excellent="< 25%" />
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-red-50 rounded-lg p-4 border border-red-200">
            <h4 className="font-bold text-red-700 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Chiffres chocs à utiliser en call
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• Un food cost à 38% au lieu de 30% = <strong>8 pts de CA perdus</strong> → sur 300k€ = 24 000€/an</li>
              <li>• Livraison 30% commission + food cost 32% = marge brute 38%. Enlève staff, loyer, charges : <strong>il reste rien</strong></li>
              <li>• <strong>80% des restaurateurs</strong> ne connaissent pas leur prime cost en temps réel</li>
              <li>• Turn-over en restauration : <strong>80-100%/an</strong>. Coût remplacement : 3 000 à 6 000€</li>
              <li>• No-show 4 couverts à 35€ = 140€/service. 3 services/semaine = <strong>21 840€/an perdus</strong></li>
            </ul>
          </div>
        </Section>

        {/* Trame du call */}
        <Section title="TRAME DU CALL (60-75 minutes)" icon="📞">
          <div className="space-y-4">
            <ScriptBlock
              phase="PHASE 1"
              duration="5 MIN"
              title="INTRODUCTION & CADRAGE"
              content={`Bonjour [Prénom], ravi de te retrouver.

Avant qu'on commence — je vais te dire exactement comment ça va se passer.

On a 60 minutes ensemble. Je vais te poser des questions sur ton restaurant, ta situation, tes chiffres. Sois le plus précis possible — plus tu l'es, plus je pourrai t'apporter quelque chose de concret aujourd'hui.

À la fin, soit je pense qu'on peut vraiment t'aider et je t'explique comment on travaille. Soit je pense que ce n'est pas le bon moment ou le bon profil, et je te le dis franchement.

Ça te va ?`}
              note="Ce cadrage est essentiel. Il installe ta légitimité et crée la permission de poser des questions directes."
              copyText={`Bonjour [Prénom], ravi de te retrouver.

Avant qu'on commence — je vais te dire exactement comment ça va se passer.

On a 60 minutes ensemble. Je vais te poser des questions sur ton restaurant, ta situation, tes chiffres. Sois le plus précis possible — plus tu l'es, plus je pourrai t'apporter quelque chose de concret aujourd'hui.

À la fin, soit je pense qu'on peut vraiment t'aider et je t'explique comment on travaille. Soit je pense que ce n'est pas le bon moment ou le bon profil, et je te le dis franchement.

Ça te va ?`}
            />

            <ScriptBlock
              phase="PHASE 2"
              duration="10 MIN"
              title="DÉCOUVERTE DU RESTAURANT"
              content={`Parle-moi de ton restaurant. C'est quoi le concept, combien de couverts, depuis quand t'es ouvert ?

[ Il présente ]

Et en termes de CA mensuel, t'es sur quelle fourchette en ce moment ?

Questions complémentaires selon ses réponses :
• « T'as combien de personnes en cuisine ? En salle ? »
• « Tu fais de la livraison ? Quel pourcentage de ton CA ? »
• « T'as des associés ? Tu tournes seul ? »
• « T'es ouvert combien de jours par semaine ? »`}
              note="Objectif : cartographier le business. Écouter sans interrompre. Prendre des notes."
              copyText={`Parle-moi de ton restaurant. C'est quoi le concept, combien de couverts, depuis quand t'es ouvert ?

Et en termes de CA mensuel, t'es sur quelle fourchette en ce moment ?`}
            />

            <ScriptBlock
              phase="PHASE 3"
              duration="15 MIN"
              title="ANALYSE DU DIAGNOSTIC"
              content={`J'ai ton score de diagnostic sous les yeux.

Avant que je t'en parle — toi, quand tu as vu les résultats, qu'est-ce qui t'a le plus frappé ?

Selon les piliers faibles du diagnostic, creuser avec :

📦 Food Cost faible
« Ton food cost, t'es à combien en ce moment ? Tu le suis semaine par semaine ou plutôt en fin de mois ? »

👥 Masse salariale faible
« Ta masse salariale représente quel % de ton CA ? T'as des heures supp non payées qui traînent ? »

📱 Livraison faible
« Quelle plateforme tu utilises ? T'as négocié ton taux de commission ? Tu sais ce que tu gagnes réellement par commande ? »

🎯 Marketing faible
« Tes nouveaux clients, ils viennent d'où principalement ? T'as une base de clients récurrents ? »`}
              note="Laisse-le parler. Ce qu'il identifie lui-même est plus puissant que ce que tu lui annonces."
              copyText={`J'ai ton score de diagnostic sous les yeux.

Avant que je t'en parle — toi, quand tu as vu les résultats, qu'est-ce qui t'a le plus frappé ?`}
            />

            <ScriptBlock
              phase="PHASE 4"
              duration="10 MIN"
              title="CREUSER LA DOULEUR"
              content={`Si je résume ce que tu viens de me dire — [reformuler le problème principal avec ses mots].

C'est juste comme résumé ?

[ Il confirme ]

Et ça, ça dure depuis combien de temps ?

Concrètement, ça t'impacte comment au quotidien ? Financièrement, mais aussi personnellement ?

[ Il répond ]

Et ça, ça représente combien en euros par mois à ton avis ?

Et si dans 12 mois ton restaurant est exactement dans le même état qu'aujourd'hui — ça te va ou ça doit absolument changer ?`}
              note="La reformulation est capitale. Elle montre que tu as écouté et elle amplifie la douleur. Faire chiffrer la perte — c'est lui qui pose le prix du problème."
              copyText={`Si je résume ce que tu viens de me dire — [reformuler le problème principal avec ses mots].

C'est juste comme résumé ?

Et ça, ça dure depuis combien de temps ?

Concrètement, ça t'impacte comment au quotidien ? Financièrement, mais aussi personnellement ?

Et ça, ça représente combien en euros par mois à ton avis ?

Et si dans 12 mois ton restaurant est exactement dans le même état qu'aujourd'hui — ça te va ou ça doit absolument changer ?`}
            />

            <ScriptBlock
              phase="PHASE 5"
              duration="10 MIN"
              title="PRÉSENTATION DE L'ACCOMPAGNEMENT"
              content={`Ok. Sur la base de ce que tu m'as dit, je pense qu'on peut vraiment t'aider.

Laisse-moi te présenter comment on travaille.

Structure de présentation — aller dans cet ordre :

1. Le résultat, pas la méthode : « À la fin des 90 jours, ce que les restaurateurs qu'on accompagne ont en commun : ils ont repris le contrôle de leurs chiffres, réduit leurs coûts sans baisser la qualité, et ils peuvent déléguer sans que tout s'effondre. »

2. Les 7 modules : Présenter en liant chaque module à UN de ses problèmes spécifiques mentionnés pendant le call.

3. Le format : « Biweekly calls, support Telegram entre les sessions, travail sur tes vrais chiffres — pas des théories. »

4. La garantie : « Si à 30 jours t'estimes que ça n'apporte rien de concret, on te rembourse. »

5. Le profil qu'on accompagne : « On prend pas tout le monde. On accompagne des restaurateurs qui ont déjà prouvé un concept et qui veulent scaler — pas des projets en création. »`}
              copyText={`Ok. Sur la base de ce que tu m'as dit, je pense qu'on peut vraiment t'aider.

Laisse-moi te présenter comment on travaille.

À la fin des 90 jours, ce que les restaurateurs qu'on accompagne ont en commun : ils ont repris le contrôle de leurs chiffres, réduit leurs coûts sans baisser la qualité, et ils peuvent déléguer sans que tout s'effondre.`}
            />

            <ScriptBlock
              phase="PHASE 6"
              duration="5 MIN"
              title="VÉRIFICATION DE L'ADÉQUATION"
              content={`Avant qu'on parle de la suite — sur ce que je t'ai présenté, c'est quoi le point qui te parle le plus ?

[ Il répond ]

Et c'est quoi le point sur lequel t'aurais envie d'en savoir plus ?`}
              note="Ces questions montrent où est son intérêt réel. Elles t'aident à personnaliser le closing."
              copyText={`Avant qu'on parle de la suite — sur ce que je t'ai présenté, c'est quoi le point qui te parle le plus ?

Et c'est quoi le point sur lequel t'aurais envie d'en savoir plus ?`}
            />

            <ScriptBlock
              phase="PHASE 7"
              duration="15 MIN"
              title="CLOSING"
              content={`Sur la base de ce qu'on vient de se dire — t'as perdu [chiffre qu'il a lui-même mentionné] par mois sur [son problème principal].

Sur 90 jours, si on résout ça ensemble, t'es sur un retour de combien à ton avis ?

[ Il répond ]

Exactement. Et l'investissement pour l'accompagnement, c'est [prix].

La question c'est pas est-ce que c'est cher — la question c'est est-ce que ça vaut le coup par rapport à ce que tu perds aujourd'hui.

Qu'est-ce qui t'empêche de démarrer maintenant ?`}
              note="La dernière question fait sortir la vraie objection. Ne pas parler avant qu'il ait répondu."
              copyText={`Sur la base de ce qu'on vient de se dire — t'as perdu [chiffre] par mois sur [problème principal].

Sur 90 jours, si on résout ça ensemble, t'es sur un retour de combien à ton avis ?

Exactement. Et l'investissement pour l'accompagnement, c'est [prix].

La question c'est pas est-ce que c'est cher — la question c'est est-ce que ça vaut le coup par rapport à ce que tu perds aujourd'hui.

Qu'est-ce qui t'empêche de démarrer maintenant ?`}
            />
          </div>
        </Section>

        {/* Objections */}
        <Section title="OBJECTIONS & RÉPONSES" icon="🚫">
          <div className="space-y-3">
            {objections.map((obj, i) => (
              <ObjectionCard
                key={i}
                objection={obj.objection}
                response={obj.response}
                isOpen={openObjection === i}
                onClick={() => setOpenObjection(openObjection === i ? null : i)}
              />
            ))}
          </div>
        </Section>

        {/* Techniques de closing */}
        <Section title="TECHNIQUES DE CLOSING" icon="🎯">
          <div className="space-y-4">
            <ClosingCard
              title="Le closing par le coût de l'inaction"
              content={`Tu m'as dit que tu perds environ [X€/mois] sur [problème].
Ça fait [X€ x 3] sur les 90 prochains jours.
L'accompagnement c'est [prix].
La question c'est pas si ça coûte cher. C'est si tu peux te permettre de ne pas le faire.`}
            />
            <ClosingCard
              title="Le closing par la décision partielle"
              content={`Écoute, ce que je te propose c'est ça : on démarre, et tu as 30 jours pour évaluer.
Si à 30 jours t'es pas satisfait, on te rembourse. Zéro risque.
La seule chose que tu risques, c'est de trouver que ça marche.`}
            />
            <ClosingCard
              title="Le closing par la pénurie"
              content={`Je veux être transparent avec toi : Sabril accompagne un nombre limité de restaurants en parallèle pour maintenir la qualité.
En ce moment il reste [X] places pour ce trimestre.
Si tu veux démarrer en [mois], il faut qu'on confirme aujourd'hui.`}
            />
            <ClosingCard
              title="Le closing silencieux"
              content={`Après avoir annoncé le prix et expliqué la valeur — se taire complètement.
Ne pas remplir le silence. Le premier qui parle perd sa position de négociation.
→ Attendre. Même 30 secondes de silence. C'est normal et c'est puissant.`}
            />
          </div>
        </Section>

        {/* Situations particulières */}
        <Section title="SITUATIONS PARTICULIÈRES" icon="⚠️">
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Le prospect n'a pas ses chiffres</p>
              <div className="bg-white rounded p-3 text-sm text-gray-700 border border-gray-200">
                Pas de souci — c'est en fait révélateur. La plupart des restaurateurs qui ont des problèmes de marge ne suivent pas leurs chiffres en temps réel.

                C'est précisément un des premiers chantiers qu'on met en place ensemble.

                Mais donne-moi une estimation — t'es plutôt sur 200k, 300k, 500k de CA annuel ?
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Le prospect est en mode 'tout va bien'</p>
              <div className="bg-white rounded p-3 text-sm text-gray-700 border border-gray-200">
                Super. Et si tout va bien, c'est quoi qui t'a poussé à faire le diagnostic ?

                [ Il répond ]

                Les restaurateurs les plus performants qu'on accompagne sont ceux qui vont bien et qui veulent aller encore mieux — pas ceux qui attendent d'être dans le rouge.
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Le prospect compare avec d'autres offres</p>
              <div className="bg-white rounded p-3 text-sm text-gray-700 border border-gray-200">
                C'est bien de comparer. Dis-moi — qu'est-ce que l'autre offre propose exactement ?

                [ Il répond ]

                La différence principale c'est ça : [différenciation spécifique]. Mais au-delà de la comparaison — est-ce que ce que je t'ai présenté aujourd'hui correspond à ce dont tu as besoin ?
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-bold text-gray-800 text-sm mb-2">Il dit oui puis se défile (ghosting)</p>
              <p className="text-xs text-gray-500 mb-2">Envoyer dans les 30 minutes suivant le call :</p>
              <div className="bg-white rounded p-3 text-sm text-gray-700 border border-gray-200">
                [Prénom], super échange tout à l'heure.

                Comme convenu, voici le récapitulatif de ce qu'on s'est dit et le lien pour confirmer ta place : [lien]

                Je te réserve la place jusqu'à demain 18h.
              </div>
            </div>
          </div>
        </Section>

        {/* Règles d'or */}
        <Section title="RÈGLES D'OR DU CLOSER" icon="⚡" defaultOpen>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RuleCard 
              icon="👂" 
              title="70/30" 
              description="Il parle 70% du temps. Toi 30%." 
            />
            <RuleCard 
              icon="📊" 
              title="Maîtriser les chiffres" 
              description="Food cost, prime cost, marges — par cœur." 
            />
            <RuleCard 
              icon="🔁" 
              title="Toujours reformuler" 
              description="Ses mots, pas les tiens. Amplifie la douleur." 
            />
            <RuleCard 
              icon="💶" 
              title="Faire chiffrer la perte" 
              description="C'est lui qui pose le prix de son problème." 
            />
            <RuleCard 
              icon="🤐" 
              title="Silence après le prix" 
              description="Ne jamais remplir le silence post-annonce." 
            />
            <RuleCard 
              icon="🚫" 
              title="Ne jamais défendre" 
              description="Reformuler, poser une question, écouter." 
            />
            <RuleCard 
              icon="📅" 
              title="Créer l'urgence réelle" 
              description="Places limitées, timing, coût de l'inaction." 
            />
            <RuleCard 
              icon="🎯" 
              title="Un seul closing par call" 
              description="Si non : fixer une date précise de rappel." 
            />
          </div>
          
          <div className="mt-6 p-4 rounded-xl bg-purple-50 border-2 border-purple-300">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-purple-600" />
              <span className="font-bold text-purple-700">Toujours finir par</span>
            </div>
            <p className="text-purple-700 font-semibold">
              Une action concrète : paiement validé, date de rappel fixée, ou "pas intéressé" clarifié.
            </p>
          </div>
        </Section>

        {/* Footer */}
        <div className="text-center pt-8 pb-12">
          <Link 
            to="/recrutement?tab=closer"
            className="inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour page Closer
          </Link>
          <p className="text-gray-400 text-xs mt-6">
            © 2025 PRSNL · Script Visio Closer v1.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default CloserCallScriptPage;
